import { useMemo, useRef } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

import type { FeedView } from '~/constants/community';
import type { CommunityPost, PaginationMeta } from '~/interfaces/community';

const COMMUNITY_POSTS_PATH = '/community/posts';

const communityPostsKeys = {
  all: ['community-posts'] as const,
  feed: (spaceSlug: string | undefined, view: FeedView) =>
    [...communityPostsKeys.all, spaceSlug ?? 'global', view] as const,
};

interface CommunityPostsPage {
  posts: CommunityPost[];
  meta: PaginationMeta;
}

const fetchCommunityPostsPage = async (
  cursor: string | undefined,
  spaceSlug: string | undefined,
  view: FeedView,
): Promise<CommunityPostsPage> => {
  const params = new URLSearchParams();
  if (cursor) params.set('cursor', cursor);
  if (spaceSlug) params.set('space_slug', spaceSlug);
  params.set('view', view);
  const response = await fetch(`${COMMUNITY_POSTS_PATH}?${params}`);
  if (!response.ok) throw new Error('Failed to fetch community posts');
  return response.json();
};

interface UseCommunityPostsOptions {
  spaceSlug?: string;
  view: FeedView;
  initialPosts: CommunityPost[];
  initialMeta: PaginationMeta;
}

const useCommunityPosts = ({ spaceSlug, view, initialPosts, initialMeta }: UseCommunityPostsOptions) => {
  // The loader returned posts for the initial view; only seed React Query for that
  // exact view's queryKey. Otherwise switching views would reuse stale initialData
  // and skip the network fetch.
  const initialView = useRef(view).current;
  const isInitialView = view === initialView;

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isError, isLoading } = useInfiniteQuery({
    queryKey: communityPostsKeys.feed(spaceSlug, view),
    queryFn: ({ pageParam }) => fetchCommunityPostsPage(pageParam as string | undefined, spaceSlug, view),
    getNextPageParam: (lastPage: CommunityPostsPage) =>
      lastPage.meta.has_more ? (lastPage.meta.next_cursor ?? undefined) : undefined,
    initialPageParam: undefined as string | undefined,
    initialData: isInitialView
      ? {
          pages: [{ posts: initialPosts, meta: initialMeta }],
          pageParams: [undefined],
        }
      : undefined,
    initialDataUpdatedAt: isInitialView ? Date.now() : undefined,
    staleTime: 60_000,
    gcTime: 30 * 60 * 1000,
  });

  const posts = useMemo(() => data?.pages.flatMap((page) => page.posts) ?? [], [data]);
  const olderPostsHidden = data?.pages.at(-1)?.meta.older_posts_hidden === true;

  return { posts, olderPostsHidden, fetchNextPage, hasNextPage, isFetchingNextPage, isError, isLoading };
};

export type { CommunityPostsPage, UseCommunityPostsOptions };
export { communityPostsKeys, useCommunityPosts };
